import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { CalendarClock, MapPin, Loader2, X, Check, Video } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtDate = (d) => new Date(`${d}T12:00:00`).toLocaleDateString('en-IE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

const TeacherSlots = ({ meeting, t, onChanged }) => {
    const [busy, setBusy] = useState(false);
    const [picking, setPicking] = useState(false);
    const act = async (fn, ok) => {
        setBusy(true);
        try { await fn(); toast.success(ok); onChanged(); }
        catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
        finally { setBusy(false); setPicking(false); }
    };
    const pick = (s) => {
        if (t.booking) return act(() => axios.put(`${API_URL}/api/ptm/my/bookings/${t.booking.id}`, { slot_start: s.start }, { withCredentials: true }), `Moved to ${s.start} — confirmation emailed`);
        return act(() => axios.post(`${API_URL}/api/ptm/my/book`, { meeting_id: meeting.id, teacher_id: t.teacher_id, slot_start: s.start }, { withCredentials: true }), `Booked ${s.start} with ${t.teacher_name} — confirmation emailed`);
    };
    const cancel = () => window.confirm(`Cancel your ${t.booking.slot_start} meeting with ${t.teacher_name}?`) && act(() => axios.delete(`${API_URL}/api/ptm/my/bookings/${t.booking.id}`, { withCredentials: true }), 'Booking cancelled');
    const showSlots = !t.booking || picking;
    return (
        <div className="rounded-lg border border-[#E5E7EB] p-3 space-y-2" data-testid={`ptm-my-teacher-${t.teacher_id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div><p className="font-semibold text-[#1F2937]">{t.teacher_name}</p><p className="text-xs text-[#6B7280]">For: {t.students.join(', ')}</p>{t.zoom_link && <a href={t.zoom_link} target="_blank" rel="noreferrer" className="text-xs text-[#1D4ED8] inline-flex items-center gap-1 hover:underline" data-testid={`ptm-teacher-zoom-${t.teacher_id}`}><Video className="h-3.5 w-3.5" />Join online (Zoom)</a>}</div>
                {t.booking ? (
                    <div className="flex items-center gap-2">
                        <Badge className="bg-[#047857] text-white gap-1" data-testid={`ptm-my-booked-${t.teacher_id}`}><Check className="h-3 w-3" />{t.booking.slot_start} – {t.booking.slot_end}</Badge>
                        {meeting.is_open && <><Button size="sm" variant="outline" disabled={busy} onClick={() => setPicking(p => !p)} data-testid={`ptm-my-change-${t.teacher_id}`}>{picking ? 'Keep' : 'Change time'}</Button>
                            <Button size="sm" variant="outline" className="text-[#DC2626] border-[#FECACA]" disabled={busy} onClick={cancel} data-testid={`ptm-my-cancel-${t.teacher_id}`}><X className="h-4 w-4" /></Button></>}
                    </div>
                ) : <Badge variant="outline">Not booked</Badge>}
            </div>
            {showSlots && meeting.is_open && (
                <div className="flex flex-wrap gap-1.5">
                    {t.slots.map(s => {
                        const mine = t.booking && t.booking.slot_start === s.start;
                        const off = s.disabled || s.taken || s.blocked;
                        const title = s.disabled ? 'Break' : s.taken ? 'Already booked by another guardian' : s.blocked ? 'Leave a free slot between your meetings' : '';
                        return (
                            <button key={s.start} type="button" disabled={off || busy || mine} onClick={() => pick(s)} title={title} data-testid={`ptm-my-slot-${t.teacher_id}-${s.start}`}
                                className={`px-2.5 py-1 rounded-full border text-xs font-mono transition-colors ${mine ? 'bg-[#047857] text-white border-[#047857]' : off ? 'bg-[#F3F4F6] text-[#9CA3AF] border-[#E5E7EB] line-through cursor-not-allowed' : 'bg-white text-[#064E3B] border-[#A7F3D0] hover:bg-[#ECFDF5]'}`}>
                                {s.start}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const ParentPTM = () => {
    const [meetings, setMeetings] = useState(null);
    const load = useCallback(async () => {
        try { const r = await axios.get(`${API_URL}/api/ptm/my`, { withCredentials: true }); setMeetings(r.data || []); }
        catch (e) { toast.error(e.response?.data?.detail || 'Failed to load meetings'); setMeetings([]); }
    }, []);
    useEffect(() => { load(); }, [load]);
    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="parent-ptm-page">
                <div><h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit'] flex items-center gap-2"><CalendarClock className="h-6 w-6 text-[#064E3B]" />Parents & Teachers Meeting</h1>
                    <p className="text-[#4B5563]">Book one time slot per teacher. Leave at least one free slot between two meetings so you can move between teachers.</p></div>
                {meetings === null ? <div className="flex justify-center p-10"><Loader2 className="h-6 w-6 animate-spin" /></div>
                    : meetings.length === 0 ? <Card className="card-surface"><CardContent className="p-10 text-center text-[#6B7280]" data-testid="ptm-my-empty">No upcoming meetings for your children.</CardContent></Card>
                        : meetings.map(m => (
                            <Card key={m.id} className="card-surface" data-testid={`ptm-my-meeting-${m.id}`}>
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-lg font-['Outfit']">{m.title} {m.is_open ? <Badge className="bg-[#047857] ml-1">Booking open</Badge> : <Badge variant="secondary" className="ml-1">Booking closed</Badge>}</CardTitle>
                                    <p className="text-sm text-[#4B5563]">{fmtDate(m.date)} · {m.start_time}–{m.end_time}</p>
                                    <p className="text-sm text-[#4B5563] flex items-center gap-1"><MapPin className="h-4 w-4" />{m.location}</p>
                                    <p className="text-xs text-[#6B7280]">Booking closes {String(m.booking_closes_at || '').replace('T', ' ')}</p>
                                </CardHeader>
                                <CardContent className="space-y-3">{m.teachers.map(t => <TeacherSlots key={t.teacher_id} meeting={m} t={t} onChanged={load} />)}</CardContent>
                            </Card>
                        ))}
            </div>
        </DashboardLayout>
    );
};

export default ParentPTM;
